"use client"

import React from "react"

import { useState, useMemo } from "react"
import { X, Plus, ChevronLeft, ChevronRight, ChevronDown, Save, Download, GripVertical, UserCheck, ScrollText } from "@/lib/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { SavePresetModal, LoadPresetModal } from "@/components/tag-preset-modals"
import { SetGroupSelector } from "@/components/set-group-selector"
import { SetGroupManagerDialog } from "@/components/set-group-manager-dialog"
import { useScriptsOnlyStore } from "@/lib/scripts-only-store"
import type { SetGroup } from "@/lib/tag-set-store"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTranslations } from "next-intl"

import type { TagSet } from "@/lib/tag-set-store"
export type { TagSet }

interface TagSetManagerProps {
  sets: TagSet[]
  activeSetId: string
  groups: SetGroup[]
  activeGroupId: string | null
  onSetChange: (setId: string) => void
  onSetCreate: (tags: string[]) => void
  onSetRemove: (setId: string) => void
  onSetUpdate: (setId: string, tags: string[]) => void
  onSetRename?: (setId: string, newName: string) => void
  onGroupChange: (groupId: string | null) => void
  onGroupCreate: (name?: string, setIds?: string[]) => void
  onGroupDelete: (groupId: string) => void
  onGroupRename: (groupId: string, name: string) => void
  onReorderSets: (groupId: string, setIds: string[]) => void
  onMoveSetToGroup: (setId: string, fromGroupId: string | null, toGroupId: string | null) => void
  className?: string
  currentTags?: string[]
  onTagsLoad?: (tags: string[], name: string) => void
  followingOnly?: boolean
  followingCount?: number
  onFollowingToggle?: () => void
}

interface SortableSetItemProps {
  set: TagSet
  isActive: boolean
  onSelect: () => void
  onRemove: () => void
  onDoubleClick: () => void
  canRemove: boolean
}

function SortableSetItem({ set, isActive, onSelect, onRemove, onDoubleClick, canRemove }: SortableSetItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: set.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-0.5">
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-secondary/50 rounded"
      >
        <GripVertical className="h-3 w-3 text-muted-foreground" />
      </div>
      <button
        onClick={onSelect}
        onDoubleClick={onDoubleClick}
        className={cn(
          "flex items-center gap-1 px-2 py-0.5 rounded border transition-all whitespace-nowrap text-xs",
          isActive
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-secondary/75 border-border/50 hover:bg-secondary/85 text-foreground hover:text-foreground",
        )}
      >
        <span className="font-medium text-current">{set.name}</span>
        <Badge variant={isActive ? "secondary" : "outline"} className="text-[10px] h-3 px-1">
          {set.tags.length}
        </Badge>
      </button>
      {canRemove && (
        <Button
          variant="ghost"
          size="sm"
          className="h-3 w-3 p-0 hover:bg-transparent"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
        >
          <X className="h-2.5 w-2.5" />
        </Button>
      )}
    </div>
  )
}

export function TagSetManager({
  sets,
  activeSetId,
  groups,
  activeGroupId,
  onSetChange,
  onSetCreate,
  onSetRemove,
  onSetUpdate,
  onSetRename,
  onGroupChange,
  onGroupCreate,
  onGroupDelete,
  onGroupRename,
  onReorderSets,
  onMoveSetToGroup,
  className,
  currentTags = [],
  onTagsLoad,
  followingOnly = false,
  followingCount = 0,
  onFollowingToggle,
}: TagSetManagerProps) {
  const t = useTranslations("tagSetManager")
  const [isOpen, setIsOpen] = useState(false)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const [showSavePreset, setShowSavePreset] = useState(false)
  const [showLoadPreset, setShowLoadPreset] = useState(false)
  const [showGroupManager, setShowGroupManager] = useState(false)
  const [editingSetId, setEditingSetId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState("")
  const scriptsOnly = useScriptsOnlyStore((s) => s.scriptsOnly)
  const toggleScriptsOnly = useScriptsOnlyStore((s) => s.toggleScriptsOnly)
  const containerRef = React.useRef<HTMLDivElement>(null)
  // "All Sets (N)" → "All (N)" collision check — see the effect below for
  // why this measures the row's flex-1 spacer rather than the row itself.
  const headerSpacerRef = React.useRef<HTMLDivElement>(null)
  const [condenseSetLabel, setCondenseSetLabel] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Filter sets based on active group
  const visibleSets = useMemo(() => {
    if (!activeGroupId) {
      return sets // Show all sets
    }

    const group = groups.find((g) => g.id === activeGroupId)
    if (!group) return sets

    // Filter and maintain group's custom order
    return group.setIds.map((id) => sets.find((s) => s.id === id)).filter(Boolean) as TagSet[]
  }, [sets, groups, activeGroupId])

  const activeSet = visibleSets.find((s) => s.id === activeSetId)
  const activeIndex = visibleSets.findIndex((s) => s.id === activeSetId)

  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && activeIndex < visibleSets.length - 1) {
      onSetChange(visibleSets[activeIndex + 1].id)
    }
    if (isRightSwipe && activeIndex > 0) {
      onSetChange(visibleSets[activeIndex - 1].id)
    }
  }

  // "All Sets (N)" → "All (N)" — condense the SetGroupSelector's label only
  // when this row is actually tight, not at a guessed pixel breakpoint.
  //
  // Measures the row's own `flex-1` spacer (the empty div between
  // SetGroupSelector and the Save/Download/Following buttons), not the row
  // itself: el.scrollWidth is defined as max(contentWidth, clientWidth), so
  // it can never report LESS than clientWidth — there is no such thing as
  // "negative overflow" via that formula, for any element, regardless of its
  // overflow property. That makes scrollWidth unusable as a slack signal:
  // it can tell you the row is too tight, but can never tell you it has
  // become roomy again, so an un-condense threshold built on it can never
  // fire. The spacer has no such floor — every other child either has
  // shrink-0 (the icon buttons) or a real min-content width (the text
  // labels), so under pressure the spacer is what actually gets squeezed
  // toward 0 first, and it freely grows past that once there's room. Its
  // own width IS the slack, directly.
  //
  // Two-threshold hysteresis, not one: condensing frees ~30-40px (the
  // "All Sets"→"All" saving), which immediately flows into the spacer. If
  // the un-condense threshold were lower than that saving, condensing would
  // immediately trigger its own reversal. UNCONDENSE_PX is set above that
  // saving with margin, so a single condense action can never alone cross
  // back over it — only genuine extra room (the row/viewport actually
  // widening) can.
  React.useEffect(() => {
    const el = headerSpacerRef.current
    if (!el) return
    const CONDENSE_PX = 8
    const UNCONDENSE_PX = 56

    const check = () => {
      const width = el.getBoundingClientRect().width
      setCondenseSetLabel((prev) => (prev ? width < UNCONDENSE_PX : width <= CONDENSE_PX))
    }

    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "ArrowLeft" && activeIndex > 0) {
          e.preventDefault()
          onSetChange(visibleSets[activeIndex - 1].id)
        } else if (e.key === "ArrowRight" && activeIndex < visibleSets.length - 1) {
          e.preventDefault()
          onSetChange(visibleSets[activeIndex + 1].id)
        }
      }
    }

    window.addEventListener("keydown", handleKeyPress)
    return () => window.removeEventListener("keydown", handleKeyPress)
  }, [activeIndex, visibleSets, onSetChange])

  const navigateSet = (direction: "prev" | "next") => {
    if (direction === "prev" && activeIndex > 0) {
      onSetChange(visibleSets[activeIndex - 1].id)
    } else if (direction === "next" && activeIndex < visibleSets.length - 1) {
      onSetChange(visibleSets[activeIndex + 1].id)
    }
  }

  const handleDoubleClick = (setId: string, currentName: string) => {
    setEditingSetId(setId)
    setEditingName(currentName)
  }

  const handleSaveRename = (setId: string) => {
    if (editingName.trim() && onSetRename) {
      onSetRename(setId, editingName.trim())
    }
    setEditingSetId(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent, setId: string) => {
    if (e.key === "Enter") {
      handleSaveRename(setId)
    } else if (e.key === "Escape") {
      setEditingSetId(null)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = visibleSets.findIndex((s) => s.id === active.id)
      const newIndex = visibleSets.findIndex((s) => s.id === over.id)

      const newSetIds = arrayMove(
        visibleSets.map((s) => s.id),
        oldIndex,
        newIndex,
      )

      // If we're in a group, reorder within that group
      if (activeGroupId) {
        onReorderSets(activeGroupId, newSetIds)
      }
      // If showing all sets, we can't reorder (would need to implement global ordering)
    }
  }

  const handleCreateGroup = () => {
    // Create empty group with auto-generated name
    onGroupCreate(undefined, [])
  }

  return (
    <>
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className={cn("bg-card border border-border/50 rounded-md glass-strong", className)}
      >
        <div data-testid="set-group-header-row" className="flex items-center gap-2 p-1 bg-secondary/15">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0 hover:bg-secondary/50">
              <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
            </Button>
          </CollapsibleTrigger>

          <span className="text-xs font-medium text-foreground">
            {t("setsLength", { length: visibleSets.length })}
          </span>

          <div className="h-4 w-px bg-border/50" />

          <SetGroupSelector
            groups={groups}
            activeGroupId={activeGroupId}
            sets={sets}
            onGroupChange={onGroupChange}
            onCreateGroup={handleCreateGroup}
            onManageGroups={() => setShowGroupManager(true)}
            onSetCreate={onSetCreate}
            currentTags={currentTags}
            condensed={condenseSetLabel}
          />

          <div ref={headerSpacerRef} data-testid="set-group-header-spacer" className="flex-1" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSavePreset(true)}
            className="h-6 w-6 p-0 shrink-0 hover:bg-secondary/50 bg-secondary/40 border border-border/30"
            title={t("saveCurrentTagsAsPreset")}
          >
            <Save className="h-3 w-3" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLoadPreset(true)}
            className="h-6 w-6 p-0 shrink-0 hover:bg-secondary/50 bg-secondary/40 border border-border/30"
            title={t("loadSavedTagPreset")}
          >
            <Download className="h-3 w-3" />
          </Button>

          {/* Following toggle with badge */}
          {onFollowingToggle && (
            <div className="relative shrink-0 flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onFollowingToggle}
                aria-pressed={followingOnly}
                className={cn(
                  "h-6 w-6 p-0 border border-border/30",
                  followingOnly
                    ? "text-white hover:brightness-110 border-violet-600"
                    : "hover:bg-violet-500/10 bg-secondary/40"
                )}
                style={followingOnly ? { backgroundColor: "#7c3aed" } : undefined}
                title={followingOnly ? t("showAllPosts") : t("showFollowingOnly")}
              >
                <UserCheck className="h-3 w-3" />
              </Button>
              {!followingOnly && followingCount > 0 && (
                <span className="absolute -top-1 -right-1 h-3.5 min-w-3.5 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center px-0.5 pointer-events-none">
                  {followingCount > 99 ? "99+" : followingCount}
                </span>
              )}
            </div>
          )}

          {/* Scripts Only — swaps the feed to content that has an attached
              script. Placed beside the Following toggle since both are
              feed-wide content filters, not set/group management — a light
              purple tint (matching the "All Sets" dropdown) marks it as a
              filter rather than another icon in the row. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleScriptsOnly()}
            aria-pressed={scriptsOnly}
            className={cn(
              "h-6 w-6 p-0 shrink-0 border",
              scriptsOnly
                ? "border-purple-600 text-white hover:brightness-110"
                : "border-purple-400/30 bg-purple-500/15 hover:bg-purple-500/30",
            )}
            style={scriptsOnly ? { backgroundColor: "#a855f7" } : undefined}
            title={t("scriptsOnlyShowOnlyContentWithScripts")}
          >
            <ScrollText className="h-3 w-3" />
          </Button>
        </div>

        <CollapsibleContent>
          <div
            ref={containerRef}
            className="flex items-center gap-2 p-1 pt-0 bg-secondary/15"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateSet("prev")}
              disabled={activeIndex === 0}
              className="h-6 w-6 p-0 shrink-0 hover:bg-secondary/50 bg-secondary/40 border border-border/30 disabled:opacity-50"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>

            <div className="flex-1 overflow-hidden">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={visibleSets.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
                  <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin">
                    {visibleSets.map((set) => (
                      <div key={set.id}>
                        {editingSetId === set.id ? (
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onBlur={() => handleSaveRename(set.id)}
                            onKeyDown={(e) => handleKeyDown(e, set.id)}
                            autoFocus
                            className="px-2 py-0.5 rounded border border-primary bg-primary text-primary-foreground text-xs font-medium w-24"
                          />
                        ) : (
                          <SortableSetItem
                            set={set}
                            isActive={set.id === activeSetId}
                            onSelect={() => onSetChange(set.id)}
                            onRemove={() => onSetRemove(set.id)}
                            onDoubleClick={() => handleDoubleClick(set.id, set.name)}
                            canRemove={visibleSets.length > 1}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateSet("next")}
              disabled={activeIndex === visibleSets.length - 1}
              className="h-6 w-6 p-0 shrink-0 hover:bg-secondary/50 bg-secondary/40 border border-border/30 disabled:opacity-50"
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <SavePresetModal isOpen={showSavePreset} onClose={() => setShowSavePreset(false)} currentTags={currentTags} />
      <LoadPresetModal
        isOpen={showLoadPreset}
        onClose={() => setShowLoadPreset(false)}
        onLoadPreset={(tags, name) => onTagsLoad?.(tags, name)}
      />
      <SetGroupManagerDialog
        isOpen={showGroupManager}
        onClose={() => setShowGroupManager(false)}
        groups={groups}
        sets={sets}
        onCreateGroup={onGroupCreate}
        onDeleteGroup={onGroupDelete}
        onRenameGroup={onGroupRename}
        onReorderSets={onReorderSets}
        onMoveSetToGroup={onMoveSetToGroup}
      />
    </>
  )
}
